"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ScanLine,
  CheckCircle2,
  Bell,
  FileText,
  Users,
  TrendingUp,
  X,
  FileBarChart,
  UserPlus,
  Search,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Mail,
  Smartphone,
} from "lucide-react";

// --- DEMO DATA & STEPS ---
const DEMO_STEPS = [
  {
    id: "residents",
    category: "Admin Dashboard",
    title: "One Directory. Total Clarity.",
    subtitle: "Resident Management",
    desc: "Start here. See every flat, every role, and every live status. No more guessing who has access. Try adding a new resident.",
  },
  {
    id: "billing",
    title: "Compliance on Autopilot",
    subtitle: "Billing Engine (Form N)",
    desc: "Generate 100+ bills in seconds. The system automatically calculates Sinking Fund, Repairs, and Non-Occupancy charges based on the MCS Act.",
  },
  {
    id: "expenses",
    title: "Every Rupee Accounted For",
    subtitle: "Expense Tracking",
    desc: "Stop using notebooks. Capture petty cash and vendor payments digitally with receipt uploads. Real-time reconciliation.",
  },
  {
    id: "notices",
    title: "Impossible to Ignore",
    subtitle: "Smart Notice Board",
    desc: "Send official notices on society letterhead directly to phone screens. Track exactly who read it and who needs a reminder.",
  },
  {
    id: "delivery",
    title: "The 3-Tap Entry",
    subtitle: "Quick Delivery Mode",
    desc: "Guards don't have time to type. Select the brand, enter the flat, and verify. Watch how fast a Zomato entry happens.",
    actionRequired: true,
  },
  {
    id: "gate_scan",
    title: "The 10-Second Guest Entry",
    subtitle: "QR Security",
    desc: "For guests, just scan. No typing required. The system verifies the invite instantly. Try scanning the visitor's pass.",
    actionRequired: true,
  },
  {
    id: "reports",
    title: "Audit-Ready Transparency",
    subtitle: "Financial Reports",
    desc: "End the financial year without stress. Pull Arrears Aging reports and Monthly Ledgers in one click for your auditor.",
  },
];

export default function DemoPage() {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // --- FORM MODAL STATE ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    interest: "RWA Solutions",
  });

  // --- INTERACTIVE STATES ---
  const [deliveryState, setDeliveryState] = useState<
    "brand" | "flat" | "success"
  >("brand");
  const [scanState, setScanState] = useState<"idle" | "scanning" | "result">(
    "idle",
  );

  const step = DEMO_STEPS[currentStepIndex];

  // --- NAVIGATION HANDLERS ---
  const nextStep = () => {
    if (currentStepIndex < DEMO_STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
      setDeliveryState("brand");
      setScanState("idle");
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
      setDeliveryState("brand");
      setScanState("idle");
    }
  };

  // --- INTERACTION HANDLERS ---
  const handleDeliveryBrandClick = () => setDeliveryState("flat");
  const handleDeliveryConfirm = () => setDeliveryState("success");

  const handleGateScan = () => {
    setScanState("scanning");
    setTimeout(() => setScanState("result"), 1500);
  };

  // --- FORM HANDLER ---
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      alert("Thanks! Our team will contact you shortly.");
      setIsModalOpen(false);
    }, 2000);
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white selection:bg-orange-500/30 flex flex-col font-sans overflow-hidden">
      {/* --- HEADER --- */}
      <header className="px-4 md:px-6 py-4 flex justify-between items-center border-b border-white/5 bg-[#050505]/90 backdrop-blur-md sticky top-0 z-40 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 transition-transform group-hover:scale-105">
            <Image
              src="/brand/logo.svg"
              alt="SmartDwara Logo"
              width={40}
              height={40}
              priority
              className="w-10 h-10"
            />
          </div>
          <div>
            <span className="font-clash font-semibold text-lg block leading-none">
              Smart Dwara
            </span>
            <span className="text-[10px] text-gray-500 font-mono uppercase tracking-widest hidden md:inline-block">
              Live Experience
            </span>
          </div>
        </div>
        <Link
          href="/"
          className="text-xs font-bold text-gray-400 hover:text-white transition-colors flex items-center gap-2 border border-white/10 px-3 py-1.5 rounded-full hover:bg-white/5"
        >
          <X className="w-4 h-4" />{" "}
          <span className="hidden sm:inline">Exit Demo</span>
        </Link>
      </header>

      {/* --- CONTENT GRID --- */}
      <div className="flex-1 flex lg:grid lg:grid-cols-12 relative overflow-hidden">
        {/* === LEFT: NARRATIVE (DESKTOP ONLY) === */}
        <div className="hidden lg:flex lg:col-span-5 p-12 flex-col justify-center border-r border-white/5 relative bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-orange-900/10 via-[#050505] to-[#050505]">
          <div className="max-w-md mx-auto w-full">
            {/* Progress Ticks */}
            <div className="flex gap-1.5 mb-12">
              {DEMO_STEPS.map((s, i) => (
                <div
                  key={s.id}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === currentStepIndex
                      ? "w-12 bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]"
                      : i < currentStepIndex
                        ? "w-4 bg-orange-500/40"
                        : "w-4 bg-white/10"
                  }`}
                />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-orange-400 text-xs font-bold uppercase tracking-wider">
                    {step.subtitle}
                  </span>
                </div>

                <h1 className="text-5xl font-clash font-bold mb-6 leading-tight text-white">
                  {step.title}
                </h1>
                <p className="text-gray-400 text-lg leading-relaxed mb-10">
                  {step.desc}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex gap-4 items-center">
              <button
                onClick={prevStep}
                disabled={currentStepIndex === 0}
                className="px-6 py-3 rounded-xl border border-white/10 text-gray-400 font-bold hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                Back
              </button>

              {currentStepIndex === DEMO_STEPS.length - 1 ? (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="px-8 py-3 rounded-xl bg-white text-black font-bold hover:bg-gray-200 transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.2)]"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  className="px-8 py-3 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-600 shadow-[0_0_30px_rgba(249,115,22,0.3)] transition-all flex items-center gap-2 group"
                >
                  Next{" "}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* === RIGHT: DEVICE SIMULATOR (ADAPTIVE) === */}
        {/* Desktop: Centered Phone with border/notch/shadow.
            Mobile: Full width/height, no border, no notch. Looks like a native app.
        */}
        <div className="w-full lg:col-span-7 bg-[#0A0A0A] relative flex items-center justify-center lg:p-8">
          {/* Mobile Narrative Overlay (Toast) - FIX: Moved to Bottom */}
          <div className="lg:hidden absolute bottom-28 left-4 right-4 z-30 pointer-events-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={step.id}
                initial={{ y: 20, opacity: 0 }} // Animate from bottom
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }} // Exit to bottom
                className="bg-black/90 backdrop-blur-xl border border-white/10 p-4 rounded-2xl shadow-2xl"
              >
                <p className="text-[10px] text-orange-400 font-bold uppercase tracking-wider mb-1">
                  {step.subtitle}
                </p>
                <p className="text-sm font-medium text-white leading-snug">
                  {step.desc}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-600/5 blur-[120px] rounded-full pointer-events-none" />

          {/* THE DEVICE FRAME */}
          <motion.div
            layout
            className={`
                relative overflow-hidden z-10 transition-all duration-500
                w-full h-full border-0 rounded-none shadow-none  // Mobile Styles
                lg:w-[360px] lg:h-[720px] lg:rounded-[3rem] lg:border-[8px] lg:border-[#1a1a1a] lg:shadow-2xl // Desktop Styles
            `}
          >
            {/* Dynamic Island (Hidden on Mobile) */}
            <div className="hidden lg:flex absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-[#1a1a1a] rounded-b-2xl z-50 items-center justify-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-700" />
              <div className="w-16 h-1.5 rounded-full bg-gray-800" />
            </div>

            {/* --- SCREEN CONTENT --- */}
            <div className="w-full h-full bg-[#080808] text-white relative flex flex-col font-sans overflow-hidden">
              {/* 1. RESIDENT MANAGEMENT */}
              {step.id === "residents" && (
                <div className="flex flex-col h-full pt-20 lg:pt-12">
                  <div className="px-6 mb-6 flex justify-between items-center">
                    <h3 className="font-bold text-xl">Directory</h3>
                    <button className="bg-orange-500 p-2 rounded-full shadow-lg shadow-orange-900/40">
                      <UserPlus className="w-5 h-5 text-white" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 space-y-3 pb-24 no-scrollbar">
                    <div className="bg-[#111] p-3 rounded-xl flex items-center gap-3 text-gray-500 mb-4 border border-white/5">
                      <Search className="w-4 h-4" />{" "}
                      <span className="text-sm">Search flat or name...</span>
                    </div>

                    {[
                      {
                        flat: "A-101",
                        name: "Rahul Sharma",
                        role: "Owner",
                        status: "Active",
                        color: "text-green-500",
                        bg: "bg-green-500/10",
                      },
                      {
                        flat: "A-102",
                        name: "Priya Desai",
                        role: "Tenant",
                        status: "Invited",
                        color: "text-blue-500",
                        bg: "bg-blue-500/10",
                      },
                      {
                        flat: "B-201",
                        name: "Amit Kumar",
                        role: "Owner",
                        status: "Active",
                        color: "text-green-500",
                        bg: "bg-green-500/10",
                      },
                      {
                        flat: "B-204",
                        name: "Sneha Roy",
                        role: "Tenant",
                        status: "Pending",
                        color: "text-orange-500",
                        bg: "bg-orange-500/10",
                      },
                    ].map((user, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-[#111] p-4 rounded-2xl border border-white/5 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-400">
                            {user.flat}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-white">
                              {user.name}
                            </p>
                            <p className="text-[10px] text-gray-500 uppercase">
                              {user.role}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-1 rounded font-bold ${user.bg} ${user.color}`}
                        >
                          {user.status}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* 2. BILLING ENGINE */}
              {step.id === "billing" && (
                <div className="flex flex-col h-full bg-[#F5F5F7] text-black pt-20 lg:pt-12">
                  <div className="px-6 mb-4">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">
                      Preview Mode
                    </span>
                    <h3 className="font-serif font-bold text-2xl text-gray-900 mt-1">
                      Invoice Generation
                    </h3>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 pb-24 no-scrollbar">
                    <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-amber-500" />
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <h4 className="font-bold text-lg">Gokuldham CHS</h4>
                          <p className="text-[10px] text-gray-500">
                            Reg: BOM/HSG/1234
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-[10px] font-bold inline-block">
                            UNPAID
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between text-xs border-b border-gray-100 pb-2">
                          <span className="text-gray-500">Bill To</span>
                          <span className="font-bold">A-101 (Mr. Bhide)</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span>Service Charge</span>
                          <span className="font-mono">₹1,500</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span>Sinking Fund</span>
                          <span className="font-mono">₹250</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span>Repair Fund</span>
                          <span className="font-mono">₹750</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span>Parking (1 Car)</span>
                          <span className="font-mono">₹500</span>
                        </div>
                        <div className="flex justify-between text-xs text-orange-600 font-medium bg-orange-50 p-1 rounded">
                          <span>Non-Occupancy</span>
                          <span className="font-mono">₹150</span>
                        </div>
                      </div>

                      <div className="border-t-2 border-dashed border-gray-300 pt-4 flex justify-between items-center">
                        <span className="font-bold text-sm">Total Due</span>
                        <span className="font-black text-2xl">₹3,150</span>
                      </div>
                    </div>
                    <button className="w-full mt-6 py-4 bg-black text-white font-bold rounded-xl shadow-xl flex items-center justify-center gap-2">
                      Generate 120 Bills <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* 3. EXPENSES */}
              {step.id === "expenses" && (
                <div className="flex flex-col h-full pt-20 lg:pt-12 px-4">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-xl">Expenses</h3>
                    <span className="text-xs text-gray-400">May 2026</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-[#111] p-4 rounded-2xl border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Total Spent</p>
                      <p className="text-xl font-bold text-white">₹45,200</p>
                    </div>
                    <div className="bg-[#111] p-4 rounded-2xl border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Pending</p>
                      <p className="text-xl font-bold text-orange-500">₹12k</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                      Recent
                    </div>
                    {[
                      {
                        title: "Water Tanker",
                        cat: "Utility",
                        amt: "₹2,500",
                        icon: RefreshCw,
                      },
                      {
                        title: "Lift AMC",
                        cat: "Maintenance",
                        amt: "₹15,000",
                        icon: FileText,
                      },
                      {
                        title: "Security Salary",
                        cat: "Payroll",
                        amt: "₹22,000",
                        icon: Users,
                      },
                    ].map((exp, i) => (
                      <motion.div
                        key={i}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-[#111] p-4 rounded-2xl border border-white/5 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400">
                            <exp.icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">
                              {exp.title}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              {exp.cat}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-white">
                            {exp.amt}
                          </p>
                          <div className="text-[9px] text-orange-500 flex items-center justify-end gap-1">
                            <FileText className="w-3 h-3" /> Receipt
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="absolute bottom-24 lg:bottom-6 right-6">
                    <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-orange-900/50 text-white text-2xl font-light">
                      +
                    </div>
                  </div>
                </div>
              )}

              {/* 4. NOTICE BOARD */}
              {step.id === "notices" && (
                <div className="h-full bg-black/90 backdrop-blur-xl relative flex items-center justify-center p-6">
                  <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1000&auto=format&fit=crop')] bg-cover opacity-30" />
                  <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="bg-white/10 backdrop-blur-xl border border-white/20 p-5 rounded-3xl w-full shadow-2xl relative z-10"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                        <Bell className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-white">
                          Notice Board
                        </h4>
                        <p className="text-[10px] text-gray-300">Just now</p>
                      </div>
                    </div>
                    <div className="bg-white text-black p-4 rounded-xl shadow-lg transform rotate-1">
                      <div className="flex justify-between items-center border-b border-gray-200 pb-2 mb-2">
                        <span className="font-serif font-bold text-lg">
                          AGM Notice
                        </span>
                        <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded font-bold">
                          URGENT
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed mb-4">
                        Dear Residents, the Annual General Meeting is scheduled
                        for Sunday, 10th Feb. Attendance is mandatory for all
                        flat owners.
                      </p>
                      <div className="flex justify-between items-center">
                        <div className="h-8 w-24 bg-gray-100 rounded" />{" "}
                        <span className="text-[10px] text-blue-600 font-bold">
                          Tap to Acknowledge
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}

              {/* 5. DELIVERY ENTRY (INTERACTIVE) */}
              {step.id === "delivery" && (
                <div className="h-full flex flex-col pt-20 lg:pt-12 px-6 bg-[#080808]">
                  <h3 className="font-bold text-xl mb-1">Quick Delivery</h3>
                  <p className="text-xs text-gray-500 mb-8">
                    Select delivery partner to auto-log
                  </p>

                  {deliveryState === "brand" && (
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        "Zomato",
                        "Swiggy",
                        "Amazon",
                        "Blinkit",
                        "Zepto",
                        "Flipkart",
                      ].map((brand) => (
                        <motion.button
                          key={brand}
                          whileTap={{ scale: 0.95 }}
                          whileHover={{
                            y: -2,
                            borderColor: "rgba(249,115,22,0.3)",
                          }}
                          onClick={handleDeliveryBrandClick}
                          className="bg-[#111] border border-white/10 rounded-2xl flex flex-col items-center justify-center py-6 gap-3 transition-all duration-300 group shadow-sm hover:shadow-md hover:bg-[#151515]"
                        >
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg ${
                              brand === "Zomato"
                                ? "bg-gradient-to-br from-red-500 to-red-700"
                                : brand === "Swiggy"
                                  ? "bg-gradient-to-br from-orange-400 to-orange-600"
                                  : brand === "Blinkit"
                                    ? "bg-gradient-to-br from-yellow-300 to-yellow-500 text-black"
                                    : brand === "Zepto"
                                      ? "bg-gradient-to-br from-purple-500 to-purple-700"
                                      : "bg-gradient-to-br from-blue-500 to-blue-700"
                            }`}
                          >
                            {brand[0]}
                          </div>
                          <span className="text-[10px] font-semibold text-gray-400 group-hover:text-white transition-colors uppercase tracking-wide">
                            {brand}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                  )}

                  {deliveryState === "flat" && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-6"
                    >
                      <div className="bg-[#111] p-4 rounded-2xl border border-white/10 flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center font-bold text-xl text-white shadow-lg">
                          Z
                        </div>
                        <div>
                          <p className="text-lg font-bold text-white">Zomato</p>
                          <p className="text-xs text-gray-500">
                            Delivery Entry
                          </p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">
                            Flat Number
                          </label>
                          <div className="h-14 bg-[#111] rounded-xl border border-white/10 flex items-center px-4 text-xl font-mono text-white focus-within:border-orange-500 transition-colors">
                            A-402
                          </div>
                        </div>
                        <button
                          onClick={handleDeliveryConfirm}
                          className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl shadow-lg shadow-orange-900/40 transition-all"
                        >
                          Confirm Entry
                        </button>
                        <button
                          onClick={() => setDeliveryState("brand")}
                          className="w-full py-4 text-gray-500 hover:text-white font-bold transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {deliveryState === "success" && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="flex-1 flex flex-col items-center justify-center text-center pb-24"
                    >
                      <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(34,197,94,0.4)]">
                        <CheckCircle2 className="w-12 h-12 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Entry Logged
                      </h3>
                      <p className="text-gray-400 text-sm">
                        Resident Notified via App
                      </p>
                      <button
                        onClick={() => setDeliveryState("brand")}
                        className="mt-8 text-sm text-orange-500 font-bold hover:text-orange-400 transition-colors"
                      >
                        New Entry
                      </button>
                    </motion.div>
                  )}
                </div>
              )}

              {/* 6. GATE SCAN */}
              {step.id === "gate_scan" && (
                <div className="h-full relative">
                  {scanState === "idle" && (
                    <div className="h-full flex flex-col items-center justify-center p-6 text-center relative">
                      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550524514-c603b57a5362?q=80&w=1000&auto=format&fit=crop')] bg-cover opacity-20" />
                      <div className="z-10 bg-black/60 backdrop-blur-xl p-6 rounded-3xl border border-white/10 w-full">
                        <h3 className="font-bold text-xl mb-2">Guest Entry</h3>
                        <p className="text-sm text-gray-400 mb-8">
                          Scan QR Code from Visitor&apos;s Phone
                        </p>
                        <button
                          onClick={handleGateScan}
                          className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(249,115,22,0.6)] animate-pulse hover:scale-105 transition-transform mx-auto"
                        >
                          <ScanLine className="w-8 h-8 text-white" />
                        </button>
                        <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-4">
                          Tap to Scan
                        </p>
                      </div>
                    </div>
                  )}

                  {scanState === "scanning" && (
                    <div className="h-full bg-black flex flex-col items-center justify-center relative">
                      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550524514-c603b57a5362?q=80&w=1000&auto=format&fit=crop')] bg-cover opacity-60" />
                      <div className="w-64 h-64 border-2 border-orange-500 rounded-3xl relative z-10 flex items-center justify-center">
                        <div className="w-full h-1 bg-orange-500 absolute top-0 animate-[scan_1.5s_ease-in-out_infinite]" />
                        <p className="text-xs font-bold bg-black/50 px-2 py-1 rounded text-orange-500">
                          Scanning...
                        </p>
                      </div>
                    </div>
                  )}

                  {scanState === "result" && (
                    <motion.div
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      className="h-full bg-[#050505] p-6 flex flex-col pt-20 lg:pt-16"
                    >
                      <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-2xl flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-black">
                          <CheckCircle2 className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-white">
                            Allowed
                          </h3>
                          <p className="text-xs text-green-400">
                            Pre-approved Invite
                          </p>
                        </div>
                      </div>
                      <div className="bg-[#111] p-6 rounded-3xl border border-white/10 space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">
                              Guest
                            </p>
                            <p className="font-bold text-lg">Amit Verma</p>
                            <p className="text-xs text-gray-400">Code: #8392</p>
                          </div>
                          <div className="w-12 h-12 bg-gray-800 rounded-xl" />
                        </div>
                        <div className="h-px bg-white/5" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase">
                            Visiting
                          </p>
                          <p className="font-bold text-lg">Flat B-701</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setScanState("idle")}
                        className="w-full mt-auto py-4 bg-white text-black font-bold rounded-xl mb-24 lg:mb-0"
                      >
                        Approve Entry
                      </button>
                    </motion.div>
                  )}
                </div>
              )}

              {/* 7. REPORTS */}
              {step.id === "reports" && (
                <div className="flex flex-col h-full pt-20 lg:pt-12 px-4 bg-[#080808]">
                  <h3 className="font-bold text-xl mb-1">Financial Reports</h3>
                  <p className="text-xs text-gray-500 mb-6">
                    FY 2025-26 • Jan Report
                  </p>

                  <div className="bg-[#111] border border-white/5 p-4 rounded-2xl mb-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-sm text-gray-300">
                        Arrears Aging
                      </h4>
                      <FileBarChart className="w-4 h-4 text-orange-500" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between text-xs text-gray-500 border-b border-white/5 pb-1">
                        <span>Flat</span>
                        <span>Pending</span>
                        <span>Days</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-bold">A-101</span>
                        <span className="text-orange-500 font-mono">
                          ₹15,000
                        </span>
                        <span className="text-red-500 font-bold bg-red-500/10 px-1 rounded">
                          90+
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-bold">C-304</span>
                        <span className="text-orange-500 font-mono">
                          ₹2,500
                        </span>
                        <span className="text-yellow-500 font-bold bg-yellow-500/10 px-1 rounded">
                          30+
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#111] border border-white/5 p-4 rounded-2xl">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-sm text-gray-300">
                        Cash Flow
                      </h4>
                      <TrendingUp className="w-4 h-4 text-green-500" />
                    </div>
                    <div className="h-32 flex items-end gap-2 justify-between px-2">
                      {[40, 60, 35, 80, 50, 90].map((h, i) => (
                        <div
                          key={i}
                          className="w-full bg-green-500/20 rounded-t-sm relative h-full flex flex-col justify-end"
                        >
                          <div
                            className="w-full bg-green-500 rounded-t-sm"
                            style={{ height: `${h}%` }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <button className="w-full mt-auto mb-24 lg:mb-6 py-4 bg-[#222] text-white font-bold rounded-xl border border-white/10 flex items-center justify-center gap-2">
                    Download Excel Report
                  </button>
                </div>
              )}
            </div>

            {/* Bottom Bar Indicator (Desktop) */}
            <div className="hidden lg:block absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full z-50" />

            {/* === MOBILE NAVIGATION BAR === */}
            <div className="lg:hidden absolute bottom-0 left-0 right-0 bg-black/90 backdrop-blur-xl border-t border-white/10 p-4 flex items-center justify-between z-50 pb-8">
              <button
                onClick={prevStep}
                disabled={currentStepIndex === 0}
                className="p-3 rounded-full bg-white/5 disabled:opacity-30"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <div className="flex flex-col items-center">
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">
                  {currentStepIndex + 1} / {DEMO_STEPS.length}
                </span>
                <div className="flex gap-1 mt-1">
                  {DEMO_STEPS.map((_, i) => (
                    <div
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full ${i === currentStepIndex ? "bg-orange-500" : "bg-white/10"}`}
                    />
                  ))}
                </div>
              </div>

              {currentStepIndex === DEMO_STEPS.length - 1 ? (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white text-black px-4 py-2 rounded-full font-bold text-sm"
                >
                  Get Started
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  className="bg-orange-500 text-white p-3 rounded-full shadow-lg shadow-orange-900/50"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* --- LEAD GEN MODAL --- */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />

            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="demo-form-card bg-[#111] border border-white/10 p-6 md:p-8 rounded-3xl shadow-2xl relative z-10 w-full max-w-lg"
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>

              <h2 className="text-2xl font-bold text-white mb-2">
                Book a Live Demo
              </h2>
              <p className="text-sm text-gray-400 mb-6">
                See how Smart Dwara can transform your society.
              </p>

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Name
                    </label>
                    <div className="relative">
                      <Users className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                      <input
                        suppressHydrationWarning
                        type="text"
                        placeholder="Enter Name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Mobile
                    </label>
                    <div className="relative">
                      <Smartphone className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                      <input
                        suppressHydrationWarning
                        type="tel"
                        placeholder="Mobile Number"
                        value={formData.mobile}
                        onChange={(e) =>
                          setFormData({ ...formData, mobile: e.target.value })
                        }
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Email ID
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                    <input
                      suppressHydrationWarning
                      type="email"
                      placeholder="Enter Email Address"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    City
                  </label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-gray-600" />
                    <input
                      suppressHydrationWarning
                      type="text"
                      placeholder="Enter City"
                      value={formData.city}
                      onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                      }
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    I am interested in...
                  </label>
                  <select
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                    value={formData.interest}
                    onChange={(e) =>
                      setFormData({ ...formData, interest: e.target.value })
                    }
                  >
                    <option>RWA Solutions</option>
                    <option>Individual Home Security</option>
                    <option>Commercial Access Control</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#F97316] hover:bg-orange-600 disabled:bg-orange-800 disabled:cursor-not-allowed text-white font-bold uppercase rounded-xl transition-all shadow-lg shadow-orange-900/20 flex items-center justify-center gap-2 mt-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Book a demo <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </main>
  );
}
